import React, { useState, useEffect, useMemo } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@src/ui/popover";
import { Button } from "@src/ui/button";
import { PaperclipIcon } from "lucide-react";
import {
  ChatAttachMenuContent,
  FilterType,
  SelectableItem,
} from "./ChatAttachMenuContent";
import { AdminLibraryInput, LibraryItemType } from "@src/types.generated";
import { useChatAttachMenuLibraryItemsQuery } from "@src/gql/aiChat.generated";

export interface ChatAttachMenuProps {
  onSelectItem: (item: SelectableItem) => void;
  onUploadClick?: () => void;
  excludeLibraryItemIds?: string[];
  disabled?: boolean;
  className?: string;
}

export const ChatAttachMenu: React.FC<ChatAttachMenuProps> = ({
  onSelectItem,
  onUploadClick,
  excludeLibraryItemIds = [],
  disabled = false,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<FilterType>(null);
  const [searchValue, setSearchValue] = useState("");

  // Reset state when menu closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFilter(null);
      setSearchValue("");
    }
  }, [isOpen]);

  // Determine what to query based on current state
  const shouldQuery = isOpen && (selectedFilter || searchValue);

  const queryInput: AdminLibraryInput = useMemo(() => {
    const input: AdminLibraryInput = {
      filters: {
        hasEnglishSearchDocument: { value: true },
      },
    };

    if (searchValue) {
      input.search = { value: searchValue };
    }

    if (selectedFilter) {
      if (selectedFilter === "Course") {
        input.filters = {
          ...input.filters,
          itemTypes: { value: [LibraryItemType.Course] },
        };
      } else if (selectedFilter === "Resource") {
        input.filters = {
          ...input.filters,
          itemTypes: { value: [LibraryItemType.TrainingResource] },
        };
      }
    }

    return input;
  }, [selectedFilter, searchValue, excludeLibraryItemIds]);

  const { data, previousData, loading, error } =
    useChatAttachMenuLibraryItemsQuery({
      variables: {
        input: queryInput,
        pagination: { limit: 30 },
      },
      skip: !shouldQuery,
    });
  const _data = previousData || data;

  const selectableItems: SelectableItem[] = useMemo(() => {
    if (!_data?.AdminLibrary?.objects) return [];

    // Filter out excluded library items on the client side
    const filteredItems = _data.AdminLibrary.objects.filter(
      (libraryItem) => !excludeLibraryItemIds.includes(libraryItem.id),
    );

    return filteredItems.map((libraryItem) => ({
      libraryItem,
    }));
  }, [_data, excludeLibraryItemIds]);

  const handleSelectItem = (item: SelectableItem) => {
    onSelectItem(item);
  };
  const _onUploadClick = () => {
    setIsOpen(false);
    if (onUploadClick) {
      onUploadClick();
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal>
      <PopoverTrigger asChild>
        <Button variant="outline" disabled={disabled} className={className}>
          <PaperclipIcon size={20} className="text-gray-8" />
          Attach
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" side="top" className="p-0 w-[400px]">
        <ChatAttachMenuContent
          selectableItems={selectableItems}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          onSelectItem={handleSelectItem}
          onUploadClick={_onUploadClick}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          isLoading={loading}
        />
      </PopoverContent>
    </Popover>
  );
};
